import { fail } from './errors.js';

export const ROLES = ['HQ_ADMIN', 'CENTER_DIRECTOR', 'THERAPIST', 'GUARDIAN', 'STUDENT'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'center:manage',        // 센터 개설·요금제·모듈 (본사)
  'center:impersonate',   // 센터 화면 대리접속 (본사)
  'client:read',          // 이용자 기본정보
  'client:readSensitive', // 장애유형·연락처·주소·상담메모 (복호화)
  'client:write',
  'session:read',
  'session:write',        // 일정 등록·출결 변경
  'ledger:read',
  'ledger:write',         // 수납 등록
  'ledger:reverse',       // 환불·결제 취소
  'voucher:audit',        // 바우처 대조·부정결제 점검
  'export:download',      // 개인정보 포함 파일 다운로드
  'task:assign',
  'task:evaluate',
  'task:perform',         // 아동이 작업장 수행(보호자 기기·자녀 모드·학생)
  'family:manage',        // 자녀 등록·자녀 모드 시작(보호자)
  'task:read',
  'ai:draft',
  'audit:read',
  'contract:request',     // 요금제·모듈 신청 (센터장)
  'contract:decide',      // 계약 심사 (본사)
  'messaging:request',    // 알림톡 발신프로필·템플릿·충전 신청 (센터장)
  'messaging:decide',     // 알림톡 승인 (본사)
  'notice:read',
  'notice:publish',       // 전체 센터 공지 (본사)
  'inquiry:create',       // 1:1 문의 (센터장)
  'inquiry:answer',       // 문의 답변 (본사)
  'course:read',          // 센터 수업 보기(보호자는 공개 수업만)
  'course:manage',        // 센터 수업 만들기·편집·공개 (원장)
  'closing:manage',       // 월 결산·회의록·지급 검토 (원장)
  'payroll:manage',       // 급상여대장·임금명세서 발행 (원장 본인)
  'payroll:read',         // 발행된 내 임금명세서 보기
  'teacher:profile',      // 선생님 찾기에 낼 내 소개·받은 신청 (선생님·원장 본인)
  'teacher:approve',      // 소속 선생님 소개 공개 승인 (원장)
  'teacher:review',       // 개인 선생님 소개 공개 승인 (본사)
  'todo:use',             // 내 할 일 관리(본인 것만)
  'todo:delegate',        // 할 일을 소속 선생님에게 맡기기 (원장)
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** 본사만 가지는 권한. 센터장 권한 목록과 대리접속 권한에서 반드시 빠진다. */
const HQ_ONLY = new Set<Permission>(['center:manage', 'center:impersonate', 'contract:decide', 'messaging:decide', 'notice:publish', 'inquiry:answer', 'teacher:review']);

const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  HQ_ADMIN: [...HQ_ONLY, 'audit:read', 'voucher:audit', 'todo:use'],
  CENTER_DIRECTOR: PERMISSIONS.filter((p) => !HQ_ONLY.has(p) && p !== 'task:perform' && p !== 'family:manage'),
  THERAPIST: [
    'client:read', 'client:readSensitive', 'session:read', 'session:write', 'ledger:read',
    'task:assign', 'task:evaluate', 'task:read', 'ai:draft', 'notice:read', 'teacher:profile', 'payroll:read', 'todo:use',
  ],
  GUARDIAN: ['task:read', 'task:perform', 'family:manage', 'course:read', 'todo:use'],
  STUDENT: ['task:read', 'task:perform', 'todo:use'],
};

/**
 * 인증된 요청 주체.
 * - centerId: 소속 센터(본사는 null, 대리접속 중에는 대상 센터)
 * - clientScope: 접근 가능한 이용자 id. 'ALL'은 센터 전체(센터장).
 *   치료사는 담당 + 초기상담 배정 이용자, 보호자는 자녀만.
 * - 보호자·학생은 센터와 연결되기 전이면 centerId 가 null 이다.
 */
export interface Principal {
  userId: string;
  role: Role;
  centerId: string | null;
  clientScope: 'ALL' | readonly string[];
  /** 대리접속 중이면 세션 정보. 읽기 전용 여부를 여기서 판단한다. */
  impersonation?: { sessionId: string; mode: 'READ' | 'WRITE'; hqUserId: string };
  /** 보호자 기기의 자녀 모드: 학생 권한으로 좁혀진다(userId 는 보호자 그대로 — 기록상 보호자 기기에서 한 일). */
  childMode?: { childId: string; childName: string };
}

const WRITE_PERMISSIONS = new Set<Permission>([
  'client:write', 'session:write', 'ledger:write', 'ledger:reverse', 'task:assign', 'task:evaluate', 'task:perform',
  'contract:request', 'messaging:request', 'inquiry:create', 'course:manage', 'closing:manage',
]);
/** 원장 본인만: 대리접속(쓰기 허용이어도)으로는 빌리지 못한다. */
const DIRECTOR_ONLY = new Set<Permission>(['course:manage', 'closing:manage', 'payroll:manage', 'payroll:read', 'teacher:profile', 'teacher:approve', 'todo:use', 'todo:delegate']);

/** 대리접속 중인 본사는 센터장 권한을 빌리되, READ 모드면 쓰기 권한을 모두 뺀다. 민감정보 복호화·다운로드는 항상 불가. */
export function permissionsOf(p: Principal): ReadonlySet<Permission> {
  if (p.impersonation) {
    const borrowed = ROLE_PERMISSIONS.CENTER_DIRECTOR.filter(
      (perm) => perm !== 'client:readSensitive' && perm !== 'export:download' && perm !== 'ai:draft' && !DIRECTOR_ONLY.has(perm) &&
        (p.impersonation!.mode === 'WRITE' || !WRITE_PERMISSIONS.has(perm)),
    );
    return new Set(borrowed);
  }
  // 자녀 모드의 userId 는 보호자 본인이라, 아이가 보호자의 할 일을 보지 않도록 뺀다
  if (p.childMode) return new Set(ROLE_PERMISSIONS[p.role].filter((perm) => perm !== 'todo:use'));
  return new Set(ROLE_PERMISSIONS[p.role]);
}

export const can = (p: Principal, perm: Permission) => permissionsOf(p).has(perm);

export function requirePermission(p: Principal, perm: Permission): void {
  if (!can(p, perm)) fail('FORBIDDEN', `권한이 없습니다: ${perm}`);
}

/** 테넌트 격리: 다른 센터 데이터는 존재 여부조차 드러내지 않도록 NOT_FOUND로 응답한다. */
export function requireCenter(p: Principal, centerId: string): void {
  if (!p.centerId || p.centerId !== centerId) fail('NOT_FOUND', '대상을 찾을 수 없습니다.');
}

export function requireClientAccess(p: Principal, centerId: string, clientId: string): void {
  requireCenter(p, centerId);
  if (p.clientScope !== 'ALL' && !p.clientScope.includes(clientId)) fail('NOT_FOUND', '대상을 찾을 수 없습니다.');
}
